import { useMemo, useCallback } from "react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useStrategy } from "src/context/Strategy/useStrategy";
import { usePuckActionPlan } from "src/components/Puck/config/contexts/PuckActionPlanContext";
import { usePuckCompanyScore } from "src/components/Puck/config/contexts/PuckCompanyScoreContext";
import { useOrganizationDiagnosticFacts } from "src/api/client-api/diagnosticFacts/useOrganizationDiagnosticFacts";
import {
    type ProcessTokenOptions,
    type TokenResolverContexts,
    processTemplateTokens,
} from "src/components/Puck/config/fields/extensions/typingSuggestions/token/tokenResolver";

export const useTokenResolver = (precision: number = 3, templateText: string = "") => {
    const foodpilotContext = useFoodpilotContext();
    const actionsContext = usePuckActionPlan();
    const strategyContext = useStrategy();
    const scoreContext = usePuckCompanyScore();

    const { facts: calcFacts } = useOrganizationDiagnosticFacts(templateText);

    const contexts: TokenResolverContexts = useMemo(
        () => ({
            user: foodpilotContext?.user?.trustedUser || {},
            company: foodpilotContext?.selectedCompany || {},
            campaign: foodpilotContext?.currentCampaign || {},
            actionPlan: actionsContext || {},
            strategyData: strategyContext || {},
            scores: scoreContext || {},
            calc: calcFacts ?? {},
        }),
        [foodpilotContext, actionsContext, strategyContext, scoreContext, calcFacts],
    );

    const processToken = useCallback(
        (text: string | undefined, options?: ProcessTokenOptions): string => {
            return processTemplateTokens(text, contexts, {
                precision,
                ...options,
            });
        },
        [contexts, precision],
    );

    return {
        contexts,
        processToken,
    };
};
